import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import LoadingStates from "../constants/states/LoadingStates";
import SecureBaseStore from "./SecureBaseStore";
import SavingStates from "../constants/states/SavingStates";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";


class CheckoutViewState {
    constructor(
        loadingState,
        savingState,
        reservationRequest,
        amount,
        taxRate,
        taxesDue,
        total,
        stripeClientSecret,
        cardInputsValid,
        cardErrorMessage,
        paymentErrorMessage
    ) {
        this.loadingState = loadingState;
        this.savingState = savingState;
        this.reservationRequest = reservationRequest;
        this.amount = amount;
        this.taxRate = taxRate;
        this.taxesDue = taxesDue;
        this.total = total;
        this.stripeClientSecret = stripeClientSecret;
        this.cardInputsValid = cardInputsValid;
        this.cardErrorMessage = cardErrorMessage;
        this.paymentErrorMessage = paymentErrorMessage;
    }

    equals(other) {
        return this.loadingState === other.loadingState &&
            this.savingState === other.savingState &&
            this.reservationRequest === other.reservationRequest &&
            this.amount === other.amount &&
            this.taxRate === other.taxRate &&
            this.taxesDue === other.taxesDue &&
            this.total === other.total &&
            this.stripeClientSecret === other.stripeClientSecret &&
            this.cardInputsValid === other.cardInputsValid &&
            this.cardErrorMessage === other.cardErrorMessage &&
            this.paymentErrorMessage === other.paymentErrorMessage;
    }
}

class ImmutableCheckoutViewStateBuilder {

    constructor() {
        this.loadingState = LoadingStates.INITIALIZED;
        this.savingState = SavingStates.NOT_STARTED;
        this.reservationRequest = {};
        this.amount = 0.0;
        this.taxRate = 0.0;
        this.taxesDue = 0.0;
        this.total = 0.0;
        this.stripeClientSecret = "";
        this.cardInputsValid = true;
        this.cardErrorMessage = "";
        this.paymentErrorMessage = "";
    }

    static fromState(state) {
        return new ImmutableCheckoutViewStateBuilder()
            .setLoadingState(state.loadingState)
            .setSavingState(state.savingState)
            .setReservationRequest(state.reservationRequest)
            .setAmount(state.amount)
            .setTaxRate(state.taxRate)
            .setTaxesDue(state.taxesDue)
            .setTotal(state.total)
            .setStripeClientSecret(state.stripeClientSecret)
            .setCardInputsValid(state.cardInputsValid)
            .setCardErrorMessage(state.cardErrorMessage)
            .setPaymentErrorMessage(state.paymentErrorMessage);
    }

    setLoadingState(loadingState) {
        this.loadingState = loadingState;
        return this;
    }

    setSavingState(savingState) {
        this.savingState = savingState;
        return this;
    }

    setReservationRequest(reservationRequest) {
        this.reservationRequest = reservationRequest;
        return this;
    }

    setAmount(amount) {
        this.amount = amount;
        return this;
    }

    setTaxRate(taxRate) {
        this.taxRate = taxRate;
        return this;
    }

    setTaxesDue(taxesDue) {
        this.taxesDue = taxesDue;
        return this;
    }

    setTotal(total) {
        this.total = total;
        return this;
    }

    setStripeClientSecret(stripeClientSecret) {
        this.stripeClientSecret = stripeClientSecret;
        return this;
    }

    setCardInputsValid(cardInputsValid) {
        this.cardInputsValid = cardInputsValid;
        return this;
    }

    setCardErrorMessage(cardErrorMessage) {
        this.cardErrorMessage = cardErrorMessage;
        return this;
    }

    setPaymentErrorMessage(paymentErrorMessage) {
        this.paymentErrorMessage = paymentErrorMessage;
        return this;
    }

    build() {
        return Object.freeze(
            new CheckoutViewState(
                this.loadingState,
                this.savingState,
                this.reservationRequest,
                this.amount,
                this.taxRate,
                this.taxesDue,
                this.total,
                this.stripeClientSecret,
                this.cardInputsValid,
                this.cardErrorMessage,
                this.paymentErrorMessage));
    }
}

class CheckoutViewStore extends SecureBaseStore {

    constructor() {
        super(StoreUpdateEvents.CHECKOUT_VIEW_STORE_UPDATE, ImmutableCheckoutViewStateBuilder);
        this.subscribeToEvent(UIInteractionEvents.CHECKOUT_VIEW_MOUNTED, this.handleCheckoutViewMounted);
        this.subscribeToEvent(ActionResultEvents.CHECKOUT_SESSION_CREATED, this.handleCheckoutSessionLoaded);
        this.subscribeToEvent(UIInteractionEvents.CHECKOUT_CARD_DETAILS_CHANGED, this.handleCardDetailsChanged);
        this.subscribeToEvent(UIInteractionEvents.CHECKOUT_PAY_CLICKED, this.handlePaymentStarted);
        this.subscribeToEvent(ActionResultEvents.CHECKOUT_PAYMENT_SUCCESS, this.resetStore);
        this.subscribeToEvent(ActionResultEvents.CHECKOUT_PAYMENT_FAILED, this.handlePaymentFailure);
        this.subscribeToEvent(UIInteractionEvents.CHECKOUT_PAYMENT_FAILURE_CLEAR_CLICKED, this.handlePaymentFailureCleared);
    }

    handleCheckoutViewMounted = (topic, data) => {
        return ImmutableCheckoutViewStateBuilder.fromState(this.state)
            .setLoadingState(LoadingStates.LOADING)
            .build();
    };

    handleCheckoutSessionLoaded = (topic, data) => {
        return ImmutableCheckoutViewStateBuilder.fromState(this.state)
            .setLoadingState(LoadingStates.FINISHED_LOADING)
            .setReservationRequest(data.reservationRequest)
            .setAmount(data.amount)
            .setTaxRate(data.taxRate)
            .setTaxesDue(data.taxesDue)
            .setTotal(data.total)
            .setStripeClientSecret(data.stripeClientSecret)
            .build();
    };

    handleCardDetailsChanged = (topic, data) => {
        return ImmutableCheckoutViewStateBuilder.fromState(this.state)
            .setCardInputsValid(data.validInputs)
            .setCardErrorMessage(data.cardErrorMessage)
            .build();
    };

    handlePaymentStarted = (topic, data) => {
        return ImmutableCheckoutViewStateBuilder.fromState(this.state)
            .setSavingState(SavingStates.SAVING)
            .build();
    };

    handlePaymentFailure = (topic, data) => {
        return ImmutableCheckoutViewStateBuilder.fromState(this.state)
            .setSavingState(SavingStates.FAILED_TO_SAVE)
            .setPaymentErrorMessage(data.message)
            .build();
    };

    handlePaymentFailureCleared = (topic, data) => {
        return ImmutableCheckoutViewStateBuilder.fromState(this.state)
            .setSavingState(SavingStates.NOT_STARTED)
            .setPaymentErrorMessage("")
            .build();
    };


}

export default new CheckoutViewStore();